import {
  getShareLinkForcedThemeFromColorMode,
  getShareLinkPublicColorMode,
  type ShareLinkForcedTheme,
} from '~/components/v2/components/shared/share-link-design/shareLinkColorMode';

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/*
 * The share route's (`/a/[[...slug]]`) `getForcedTheme` implementation — attached as a page static
 * (same idiom as `getLayout`) and invoked by `_app`, which feeds the result to `AppThemeProvider`.
 * The route delivers its ISR data as `{ shortId, initialData: { presentationSettings } }` for
 * every route type — a collection (`/a/m<shortId>`, with or without a board deep-link,
 * `PublicShowcaseRouteProps`) and a board share link (`/a/b<shortId>/...`, `PublicBoardProps`)
 * both carry their own object's presentationSettings at that same path, so a direct load of any of
 * these surfaces renders the same theme as navigating to it in-page.
 *
 * `pageProps` comes in as `unknown` (the `getForcedTheme` contract — `_app` can't know each
 * route's shape), so this narrows structurally across the route's own props union instead of
 * trusting a cast. The passcode variant (`PasscodeRequiredProps`) falls out naturally: its ISR
 * props carry no `presentationSettings` (the server can never see a visitor's passcode token), so
 * it returns `undefined` here and is themed post-verification by `usePublicShareLinkForcedTheme`
 * through `useShareLinkForcedThemeStore` instead.
 */
export const getShareLinkForcedTheme = (pageProps: unknown): ShareLinkForcedTheme | undefined => {
  if (!isRecord(pageProps)) {
    return undefined;
  }

  const { shortId, initialData } = pageProps;

  // Only collection ('m'-prefixed) and board ('b'-prefixed) share links (see `isShowcaseRoute` /
  // `isBoardRoute` in ~/utils/shareUrl) can author `colorMode` today; clip/canvas links never
  // carry it, so they are not sniffed.
  if (typeof shortId !== 'string' || !(shortId.startsWith('m') || shortId.startsWith('b')) || !isRecord(initialData)) {
    return undefined;
  }

  const { presentationSettings } = initialData;

  const colorMode =
    isRecord(presentationSettings) && typeof presentationSettings.colorMode === 'string'
      ? presentationSettings.colorMode
      : undefined;

  // Absent `colorMode` (legacy links, or no presentationSettings at all) must force Light via
  // `getShareLinkPublicColorMode` — otherwise ISR first-paint follows the visitor's stored theme
  // and two people looking at the same link can disagree. `'toggle'` and unknown values still
  // pass through and leave the visitor in charge.
  return getShareLinkForcedThemeFromColorMode(getShareLinkPublicColorMode(colorMode));
};
